<script setup>
import { ref, computed, watch, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { mdiOpenInNew } from '@mdi/js';
import { fmtDate, capitalizeFirst } from '../../domain/format.js';
import { birthYearOf } from '../../domain/birthYear.js';
import { safeTransfermarktHref, resolveTransfermarktInput, TM_LINK_ATTRS } from '../../domain/transfermarkt.js';
import { POSITIONS, FEET } from '../../domain/positions.js';
import { STATUS_ORDER } from '../../domain/status.js';
import { useAuth } from '../../composables/useAuth.js';
import { useTransfers } from '../../composables/useTransfers.js';
import { hasTransferTab } from '../../domain/playerTabs.js';
import { useStoredImage } from '../../composables/useStoredImage.js';
import { variantForSize } from '../../domain/photoVariants.js';
import { useDesktop } from '../../composables/useDesktop.js';
import { photoPathFor } from '../../domain/playerPublic.js';
import PlayerAvatar from '../PlayerAvatar.vue';
import ClubField from '../ClubField.vue';
import CountrySelect from '../CountrySelect.vue';
import DateField from '../DateField.vue';
import KeywordSelect from '../KeywordSelect.vue';
import KeywordChip from '../KeywordChip.vue';
import AgeGroupChip from '../AgeGroupChip.vue';
import { AGE_GROUPS, ageGroupOf } from '../../domain/ageGroups.js';
import { normalizeTraits, keywordLabel } from '../../domain/playerKeywords.js';
import DeletePlayerButton from '../DeletePlayerButton.vue';

const props = defineProps({
  player: { type: Object, required: true },
});

/* Wunsch an das Panel, auf einen Reiter zu wechseln (PlayerPanel.vue: @show-tab) */
const emit = defineEmits(['show-tab']);

/* Transferhistorie: Dasselbe Laden wie im Panel (gemeinsame Liste, keine zweite
   Abfrage). Der Knopf "Add transfer" ist für Bearbeiter da, solange das Panel keinen
   Reiter Transfer Info zeigt (Regel: domain/playerTabs.js hasTransferTab) — sonst gäbe es
   bei Spielern im Kader keinen Weg zum ersten Eintrag. Er öffnet denselben Dialog wie
   die Historie; nach dem Speichern erscheint der Reiter, und das Panel springt dorthin. */
const TransferDialog = defineAsyncComponent(() => import('../TransferDialog.vue'));
const { items: transferItems, busy: transferBusy, error: transferError, add: addTransfer } =
  useTransfers(() => props.player.id);
const transferDialogOpen = ref(false);

/* Aufnehmender Verein samt Logo nur bei Status "transferred" — dasselbe Feld wie
   im Transfer-Reiter (ClubField.vue). */
const isTransferred = computed(() => props.player.status === 'transferred');

/* Transfermarkt-Link: nur bei transferierten Spielern, in der Ansicht unter den Stammdaten und in der
   Bearbeiten-Ansicht als Eingabefeld (Entscheidung Bastian, 2026-10-07; vorher bei allen Spielern im Reiter
   Material). Nur ein Wert, der die Regel erfüllt (https, Host Transfermarkt, domain/transfermarkt.js),
   wird zum Link; ein Altwert, der sie verletzt, steht als reiner Text da, nie als Link. Kein Wert: keine
   Zeile. Die Eingabe übernimmt nur einen gültigen Link ins Spielerobjekt (gespeichert wird mit dem
   Speichern-Knopf des Panels wie alle Stammdaten); leer entfernt ihn. */
const tmValue = computed(() => (isTransferred.value ? String(props.player.transfermarktUrl || '').trim() : ''));
const tmHref = computed(() => safeTransfermarktHref(tmValue.value));
const tmDraft = ref('');
const tmError = ref('');
function onTmInput() {
  const r = resolveTransfermarktInput(tmDraft.value);
  if (r.ok) {
    props.player.transfermarktUrl = r.url;
    tmError.value = '';
  } else {
    tmError.value = r.error; // ungültig: nichts übernehmen, der gespeicherte Wert bleibt
  }
}

const { can } = useAuth();
const { t } = useI18n();
const editing = ref(false);

/* Schlagwörter (Player Card): feste Liste, bis zu drei; Änderung nur mit Schreibrecht auf players (admin, board,
   data) — dasselbe Recht wie "Edit details". Altwerte außerhalb der Liste erscheinen als normale Chips. */
const keywordChips = computed(() => normalizeTraits(props.player.traits).map((v) => ({ value: v, label: keywordLabel(v, t) })));
watch(editing, (on) => {
  if (!on) return;
  tmDraft.value = props.player.transfermarktUrl || '';
  tmError.value = '';
});
const canEdit = computed(() => can('players', 'write'));
/* Altersklasse (U17/U19): Chip rechts neben der Position, nur wenn gesetzt. Pflege nur mit Schreibrecht (Edit details). */
const ageGroup = computed(() => ageGroupOf(props.player));
const canAddTransfer = computed(
  () =>
    canEdit.value &&
    !!props.player.id &&
    props.player._isNew !== true &&
    !hasTransferTab(props.player.status, transferItems.value.length > 0)
);

function openTransferDialog() {
  transferError.value = null;
  transferDialogOpen.value = true;
}
function closeTransferDialog() {
  transferDialogOpen.value = false;
  transferError.value = null;
}
async function onTransferSave(payload) {
  if (!(await addTransfer(payload))) return;
  closeTransferDialog();
  emit('show-tab', 'transfer');
}

/* Großer Bildplatz links neben den Daten: bevorzugt das eigens hinterlegte Foto
   (Altbestand bioPhoto/bioPhotoPath, aus der Zeit als eigenständiges
   biometrisches Foto), sonst das Spielerfoto (avatar/avatarPath). Ohne beides
   Initialen (PlayerAvatar.vue). Das Foto ändern geschieht über PlayerAvatar
   selbst (gleicher Upload, gleiche Rechte wie früher im Panel-Kopf): Es setzt
   avatarPath am Spieler; gespeichert wird über den Speichern-Knopf im Panel.
   Ein neu hochgeladenes Foto leert bioPhoto/bioPhotoPath (domain/playerPhoto.js),
   damit es hier nicht vom alten Bild verdeckt wird. */
const PHOTO_SIZE_MOBILE = 173; // Höhe des Bildplatzes in px; 130 breit (3:4)
const PHOTO_SIZE_DESKTOP = 260; // ab 1024 px: 195 breit (main.css: .info-photo-col)
const { isDesktop } = useDesktop();
const photoSize = computed(() => (isDesktop.value ? PHOTO_SIZE_DESKTOP : PHOTO_SIZE_MOBILE));
/* Akten-Foto, sonst Spielerfoto (photoPathFor); bei Gästen ist bioPhotoPath meist leer, dann gilt
   avatarPath in der Fassung md. */
const { url: bioStored } = useStoredImage(
  () => photoPathFor(props.player),
  () => variantForSize(photoSize.value)
);

/* Objekt aus der öffentlichen Ansicht (Gast): kein Geburtsdatum, nur der Jahrgang */
const publicOnly = computed(() => props.player._publicOnly === true);
const bioPhoto = computed(
  () => props.player.bioPhoto || props.player.avatar || bioStored.value
);

const height = computed(() => {
  const h = props.player.height;
  if (!h) return '';
  return /cm/i.test(h) ? h : `${h} cm`;
});

/* Bevorzugter Fuß: Anzeige mit großem Anfangsbuchstaben, gespeichert bleibt der Wert, wie er ist */
const footLabel = computed(() => capitalizeFirst(props.player.foot));

/* Größe für die Anzeige: Zahl groß, Einheit ("cm") klein und gedämpft dahinter */
const heightParts = computed(() => {
  const h = height.value;
  if (!h) return { num: '', unit: '' };
  const m = /^(.*?)\s*(cm)$/i.exec(h);
  return m ? { num: m[1], unit: m[2].toLowerCase() } : { num: h, unit: '' };
});

const position = computed(() => {
  const p = props.player;
  return p.pos2 ? `${p.pos} / ${p.pos2}` : p.pos;
});

const nationalTeam = computed(() => {
  const p = props.player;
  if (!p.nationalTeam) return '';
  return p.ntLevel ? `${p.nationalTeam} · ${p.ntLevel}` : p.nationalTeam;
});

const NT_LEVELS = ['U17', 'U20', 'U23', 'A-Team'];

/* Die Felder werden direkt am Spielerobjekt bearbeitet. Gespeichert wird erst
   über den Speichern-Knopf im Panel (PlayerPanel.onSave); Schließen mit
   offenen Änderungen fragt nach und verwirft sie dann. */
</script>

<template>
  <div>
    <!-- Im Bearbeiten-Modus oben; in der Ansicht steht der Knopf unter dem Foto
         (spart eine eigene Zeile über der Karte). -->
    <div v-if="canEdit && editing" class="sector-actions">
      <button class="btn ghost small" @click="editing = false">{{ $t('common.close') }}</button>
    </div>

    <!-- ---------- Ansicht ---------- -->
    <div v-if="!editing" class="info-card view">
      <div class="info-photo-col">
        <PlayerAvatar :player="player" :size="photoSize" :display-src="bioPhoto" large editable />
      </div>

      <!-- Datenspalte. Reihenfolge (Handy und Laptop gleich): Position, Geburtsdatum, Größe, bevorzugter Fuß; dann
           Nationalität, Nationalmannschaft, bei Grassrunners seit. Vorname und Nachname stehen schon im Titel und
           fehlen in der Ansicht (im Bearbeiten-Modus bleiben sie). Laptop: eine Spalte neben dem Foto (Leitzeile,
           Kennzahlenleiste, ruhige Zeile, Knöpfe). Handy: die Teile sind Zellen des Kartenrasters (display: contents).
           Anordnung: styles/profile.css -->
      <div class="info-data">
        <!-- Position: erste Position groß, eine zweite danach gedämpft -->
        <div class="info-item info-position f-position" :class="{ 'has-age-group': ageGroup }">
          <label>{{ $t('player.position') }}</label>
          <span :title="position">
            <span class="pos-first">{{ player.pos || '—' }}</span><span v-if="player.pos2" class="pos-second"><span class="pos-sep"> / </span>{{ player.pos2 }}</span>
          </span>
          <AgeGroupChip v-if="ageGroup" class="age-slot" :value="ageGroup" />
        </div>

        <!-- Kennzahlen: Geburtsdatum, Größe, bevorzugter Fuß (Größe und Fuß bilden ein Paar: nebeneinander, bei
             Platzmangel untereinander) -->
        <div class="info-stats">
          <div v-if="!publicOnly || player.dob" class="info-item f-dob">
            <label>{{ $t('player.dob') }}</label><span>{{ fmtDate(player.dob) || '—' }}</span>
          </div>
          <div v-else class="info-item f-dob">
            <label>{{ $t('player.birthYear') }}</label><span>{{ birthYearOf(player) || '—' }}</span>
          </div>
          <div class="info-duo">
            <div class="info-item f-height">
              <label>{{ $t('player.height') }}</label>
              <span :title="height">{{ heightParts.num || '—' }}<span v-if="heightParts.unit" class="unit"> {{ heightParts.unit }}</span></span>
            </div>
            <div class="info-item f-foot">
              <label>{{ $t('player.foot') }}</label><span :title="footLabel">{{ footLabel || '—' }}</span>
            </div>
          </div>
        </div>

        <!-- Weitere Angaben, ruhiger: Nationalität, Nationalmannschaft, bei Grassrunners seit -->
        <div class="info-fields">
          <div class="info-item f-nat">
            <label>{{ $t('player.nationality') }}</label><span :title="player.nationality">{{ player.nationality || '—' }}</span>
          </div>
          <div v-if="nationalTeam" class="info-item f-nt">
            <label>{{ $t('player.nationalTeam') }}</label><span :title="nationalTeam">{{ nationalTeam }}</span>
          </div>
          <!-- Bei transferierten Spielern nicht mehr relevant (Entscheidung Bastian, 2026-10-06): weder
               angezeigt noch bearbeitbar. Der Wert im Datensatz bleibt unangetastet. -->
          <div v-if="!isTransferred" class="info-item f-since">
            <label>{{ $t('player.atClubSince') }}</label>
            <span>{{ fmtDate(player.atGrassrunnersSince) || '—' }}</span>
          </div>
          <ClubField v-if="isTransferred" :player="player" :label="$t('player.nowAt')" readonly />
          <!-- Schlagwörter: leer = Zeile entfällt für Leser -->
          <div v-if="keywordChips.length" class="info-item wide f-keywords" data-testid="keywords-info">
            <label>{{ $t('player.keywords') }}</label>
            <span class="kw-view">
              <KeywordChip v-for="c in keywordChips" :key="c.value" :label="c.label" data-testid="keyword-chip-view" />
            </span>
          </div>
          <div v-if="tmValue" class="info-item wide f-tm" data-testid="tm-info">
            <label>{{ $t('material.transfermarkt') }}</label>
            <span>
              <a v-if="tmHref" class="video-link" :href="tmHref" :title="tmHref" v-bind="TM_LINK_ATTRS">{{ $t('material.openProfile') }}<svg class="tm-ext" viewBox="0 0 24 24" aria-hidden="true"><path :d="mdiOpenInNew" /></svg></a>
              <!-- Altwert, der die Regel verletzt: nur Text, nie ein Link -->
              <span v-else class="tm-plain" data-testid="tm-plain">{{ tmValue }}</span>
            </span>
          </div>
        </div>

        <!-- Knöpfe: klein und ruhig, linksbündig, nach den Daten -->
        <div v-if="canEdit || canAddTransfer" class="info-actions">
          <button v-if="canEdit" class="btn ghost small info-edit" @click="editing = true">
            {{ $t('player.editDetails') }}
          </button>
          <button
            v-if="canAddTransfer"
            type="button"
            class="btn ghost small info-edit"
            data-testid="add-transfer"
            @click="openTransferDialog"
          >
            {{ $t('transferHistory.add') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ---------- Bearbeiten ---------- -->
    <div v-else class="info-card edit">
      <div class="edit-grid">
        <div class="field">
          <label>{{ $t('player.firstName') }}</label>
          <input v-model="player.firstName" type="text" />
        </div>
        <div class="field">
          <label>{{ $t('player.lastName') }}</label>
          <input v-model="player.lastName" type="text" />
        </div>
        <div class="field">
          <label>{{ $t('player.position') }}</label>
          <select v-model="player.pos">
            <option v-for="pos in POSITIONS" :key="pos" :value="pos">{{ pos }}</option>
          </select>
        </div>
        <div class="field">
          <label>{{ $t('player.secondPosition') }}</label>
          <select v-model="player.pos2">
            <option value="">—</option>
            <option v-for="pos in POSITIONS" :key="pos" :value="pos">{{ pos }}</option>
          </select>
        </div>
        <div class="field">
          <label>{{ $t('player.foot') }}</label>
          <select v-model="player.foot">
            <option value="">—</option>
            <option v-for="f in FEET" :key="f" :value="f">{{ f }}</option>
          </select>
        </div>
        <div class="field">
          <label>{{ $t('player.height') }}</label>
          <input v-model="player.height" type="text" placeholder="180 cm" />
        </div>
        <div class="field">
          <label>{{ $t('player.dob') }}</label>
          <DateField v-model="player.dob" birth />
        </div>
        <!-- Altersklasse: von Hand, keine Zuordnung nach Geburtsjahr. "None" = keine Altersklasse. -->
        <div class="field" data-testid="age-group-edit">
          <label>{{ $t('ageGroup.label') }}</label>
          <select v-model="player.ageGroup" data-testid="age-group-select">
            <option value="">{{ $t('ageGroup.none') }}</option>
            <option v-for="g in AGE_GROUPS" :key="g" :value="g">{{ g }}</option>
          </select>
        </div>
        <div class="field">
          <label>{{ $t('player.nationality') }}</label>
          <CountrySelect v-model="player.nationality" />
        </div>
        <div class="field">
          <label>{{ $t('player.nationalTeam') }}</label>
          <input v-model="player.nationalTeam" type="text" />
        </div>
        <div class="field">
          <label>{{ $t('player.ntLevel') }}</label>
          <select v-model="player.ntLevel">
            <option value="">—</option>
            <option v-for="l in NT_LEVELS" :key="l" :value="l">{{ l }}</option>
          </select>
        </div>
        <div v-if="!isTransferred" class="field">
          <label>{{ $t('player.atClubSince') }}</label>
          <DateField v-model="player.atGrassrunnersSince" />
        </div>
        <div class="field">
          <label>{{ $t('player.status') }}</label>
          <select v-model="player.status">
            <option v-for="s in STATUS_ORDER" :key="s" :value="s">{{ $t(`status.${s}`) }}</option>
          </select>
        </div>

        <!-- Schlagwörter aus der festen Liste (höchstens drei) -->
        <div class="field kw-field" data-testid="keywords-edit">
          <label>{{ $t('player.keywords') }}</label>
          <KeywordSelect v-model="player.traits" />
        </div>

        <!-- Erscheint, sobald der Status auf "transferred" steht -->
        <ClubField v-if="isTransferred" :player="player" :label="$t('player.nowAt')" />

        <!-- Nur bei Transferierten (und nur hier in der Bearbeiten-Ansicht, also nur mit Schreibrecht) -->
        <div v-if="isTransferred" class="field tm-field" data-testid="tm-edit">
          <label>{{ $t('material.transfermarkt') }}</label>
          <input
            v-model="tmDraft"
            type="text"
            inputmode="url"
            autocomplete="off"
            placeholder="https://www.transfermarkt.com/…"
            :aria-invalid="!!tmError"
            @input="onTmInput"
          />
          <p v-if="tmError" class="tm-error" role="alert">{{ $t(`material.tmError.${tmError}`) }}</p>
        </div>
      </div>

      <!-- Nur admin, nur bei einem gespeicherten Spieler (DeletePlayerButton prüft beides) -->
      <DeletePlayerButton :player="player" />
    </div>

    <!-- Dialog für den ersten Transfer-Eintrag; derselbe wie in der Historie -->
    <TransferDialog
      v-if="transferDialogOpen"
      :player="player"
      :saving="transferBusy"
      :error="transferError"
      @close="closeTransferDialog"
      @save="onTransferSave"
    />
  </div>
</template>

<style scoped>
.tm-field,
.kw-field {
  grid-column: 1 / -1;
}
.kw-view {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tm-error {
  margin: 4px 0 0;
  font-size: var(--fs-xs);
  color: var(--red);
}
.tm-ext {
  width: 1em;
  height: 1em;
  margin-left: 4px;
  vertical-align: -0.125em;
  fill: currentColor;
}
.tm-plain {
  color: var(--muted);
  overflow-wrap: anywhere;
}
/* Position + Altersklasse: Beschriftung und Wert links, Chip rechts oben in der Zelle */
.info-item.has-age-group {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 8px;
  align-items: start;
}
.info-item.has-age-group > label,
.info-item.has-age-group > span {
  grid-column: 1;
}
.info-item.has-age-group > .age-slot {
  grid-column: 2;
  grid-row: 1 / span 2;
  justify-self: end;
}
.sector-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}
.info-card.edit {
  flex-direction: column;
  gap: 14px;
}
</style>
